// @ts-nocheck
import React, { useEffect, useState } from "react";
import { Autocomplete, TextField, Tooltip } from "@mui/material";
import { useStoreState } from "easy-peasy";

import FullPageLoader from "components/Loader/FullPage";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CustomModal from "components/CustomModal";
import { get, post } from "requests";
import { Add, Autorenew, Close, Expand, Fullscreen, PlayCircleFilled } from "@mui/icons-material";

interface LendPathWayTabProps {
  dealApplicationId: number;
  isReadOnly?: boolean;
  permittedTabs?: any;
}

type LendPathwayStatus = "loading" | "processing" | "success" | "error" | "not_found";

export default function LendPathWayTab({
  dealApplicationId,
  isReadOnly,
  permittedTabs,
}: LendPathWayTabProps) {
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<LendPathwayStatus>("loading");
  const [message, setMessage] = useState<string>("");
  const [retryCount, setRetryCount] = useState(0);
  const [isRunningLendPathway, setIsRunningLendPathway] = useState(false);

  const mediaAttachments = useStoreState((state: any) => state.fileUploads.mediaAttachments);
  const [files, setFiles] = useState<any[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [showLendPathwayModal, setShowLendPathwayModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (Array.isArray(mediaAttachments) && mediaAttachments.length > 0) {
      const statementsFile = mediaAttachments.filter(
        (file: any) => file.category === "Bank Statements"
      );
      setFiles(statementsFile);
    } else {
      setFiles([]);
    }
  }, [mediaAttachments]);

  useEffect(() => {
    fetchEmbedUrl();
  }, [dealApplicationId]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;

    if (status === "processing") {
      intervalId = setInterval(() => {
        console.log(`Auto-retrying fetchEmbedUrl (attempt ${retryCount + 1})`);
        fetchEmbedUrl();
        setRetryCount((prev) => prev + 1);
      }, 10000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [status, retryCount]);

  const fetchEmbedUrl = async () => {
    try {
      setStatus("loading");
      setMessage("Requesting embed URL from server…");

      const response = await get(`integration/lend-pathway/getEmbedURL/${dealApplicationId}`);

      const apiStatus = response?.status;
      const apiMessage = response?.message;
      const apiData = response?.data;

      if (apiStatus === "success") {
        setEmbedUrl(apiData?.embed_url || null);
        setStatus("success");
        setRetryCount(0);
        return;
      }

      if (apiStatus === "processing") {
        setStatus("processing");
        setMessage("LendPathway is still processing the statements. Please try again later.");
        return;
      }

      if (
        apiStatus === "not_found" ||
        apiMessage?.toLowerCase().includes("not found") ||
        apiMessage?.toLowerCase().includes("not_found")
      ) {
        setStatus("not_found");
        setMessage("LendPathway analysis has not been run yet for this application.");
        setRetryCount(0);
        return;
      }

      setStatus("error");
      setMessage(apiMessage || "Failed to load embed URL.");
      setRetryCount(0);
    } catch (err) {
      console.error("Error fetching embed URL:", err);
      setStatus("error");
      setMessage("Unable to load embed URL.");
      setRetryCount(0);
    }
  };

  const runLendPathway = async () => {
    if (selectedFiles.length === 0) {
      setMessage("Please select at least one statement file before running LendPathway.");
      return;
    }

    try {
      setStatus("loading");
      setIsRunningLendPathway(true);
      setMessage("Starting LendPathway analysis...");

      const response = await post(
        `integration/lend-pathway/extractStatements`,
        {
          appId: dealApplicationId,
          mediaAttachmentIds: selectedFiles,
        },
        { timeout: 0 }
      );

      const apiStatus = response?.status;
      const apiMessage = response?.message;

      if (apiStatus === "success" || apiStatus === "processing") {
        setStatus("processing");
        setMessage(
          apiMessage ||
            "LendPathway analysis has been started and is now processing your statements."
        );
        fetchEmbedUrl();
      } else {
        setStatus("error");
        setMessage(apiMessage || "Failed to start LendPathway analysis.");
      }
    } catch (err) {
      console.error("Error running LendPathway:", err);
      setStatus("error");
      setMessage("Unable to start LendPathway analysis.");
    } finally {
      setIsRunningLendPathway(false);
      setShowLendPathwayModal(false);
      setSelectedFiles([]);
    }
  };

  const handleFileSelect = (event: any, newValue: any) => {
    setSelectedFiles(newValue.map((option: any) => option.value));
  };

  if (status === "loading") {
    return <FullPageLoader />;
  }

  const customModal= (
  <CustomModal
    open={showLendPathwayModal}
    onClose={() => {
      setSelectedFiles([]);
      setShowLendPathwayModal(false);
    }}
    title="Upload Bank Statements (Lend Pathway)"
    cancelText="Cancel"
    submitText="Submit"
    onCancel={() => {
      setSelectedFiles([]);
      setShowLendPathwayModal(false);
    }}
    onSubmit={runLendPathway}
    maxWidth="sm"
    fullWidth
  >
    <MDBox display="flex" flexDirection="column" width="100%">
      <Autocomplete
        multiple
        id="file-select"
        options={files.map((file) => ({
          value: file.id,
          label: file.name,
        }))}
        getOptionLabel={(option) => option.label}
        value={files
          .filter((file) => selectedFiles.includes(file.id))
          .map((file) => ({
            value: file.id,
            label: file.name,
          }))}
        onChange={handleFileSelect}
        disableCloseOnSelect
        renderInput={(params) => <TextField {...params} label="Select Files" />}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        sx={{ mt: 2, mb: 2 }}
      />
    </MDBox>
  </CustomModal>)

  if (status === "processing") {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        p={3}
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <MDBox position="relative" mb={3}>
          <MDBox
            sx={{
              width: 80,
              height: 80,
              border: "4px solid",
              borderColor: "grey.300",
              borderTopColor: "primary.main",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <MDBox
            position="absolute"
            top="50%"
            left="50%"
            sx={{ transform: "translate(-50%, -50%)" }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path
                d="M13 10V3L4 14h7v7l9-11h-7z"
                stroke="currentColor"
                strokeWidth={2}
                fill="currentColor"
              />
            </svg>
          </MDBox>
        </MDBox>

        <MDBox textAlign="center" mb={2}>
          <MDTypography
            variant="h4"
            gutterBottom
            sx={{ color: "text.primary", fontWeight: "bold" }}
          >
            Processing Your Statements
          </MDTypography>
          <MDTypography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: "400px",
              lineHeight: 1.6,
            }}
          >
            LendPathway is carefully analyzing your financial statements. This usually takes just a
            few moments.
          </MDTypography>
        </MDBox>

        <MDButton
          variant="contained"
          color="primary"
          onClick={fetchEmbedUrl}
          startIcon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                stroke="currentColor"
                strokeWidth={2}
                fill="none"
              />
            </svg>
          }
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: "bold",
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-1px)",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          Check Now
        </MDButton>

        <MDTypography variant="caption" sx={{ color: "text.secondary", mt: 2 }}>
          Last checked: {new Date().toLocaleTimeString()}
        </MDTypography>
      </MDBox>
    );
  }

  if (status === "not_found" || status === "error") {
    return (
      <>
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="flex-end"
          minHeight="120px"
          p={3}
        >
          <MDButton
            variant="contained"
            color="primary"
            onClick={() => {
              setSelectedFiles([]);
              setShowLendPathwayModal(true);
            }}
            disabled={isRunningLendPathway}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 1,
              textTransform: "none",
              fontWeight: "bold",
              boxShadow: 2,
              "&:hover": {
                boxShadow: 3,
              },
            }}
            startIcon={
              isRunningLendPathway ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="none"
                  />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="currentColor"
                  />
                  <path
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="none"
                  />
                </svg>
              )
            }
          >
            {isRunningLendPathway ? "Starting..." : "Run LendPathway"}
          </MDButton>
        </MDBox>

        {status === "error" && (
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="300px"
            p={4}
            sx={{
              backgroundColor: "background.paper",
              borderRadius: 2,
              boxShadow: 1,
              border: "1px solid",
              borderColor: "error.light",
            }}
          >
            <MDBox sx={{ color: "error.main", mb: 2 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
            </MDBox>

            <MDTypography variant="h5" color="error" gutterBottom sx={{ fontWeight: "bold" }}>
              Error
            </MDTypography>

            <MDTypography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, textAlign: "center" }}
            >
              {message}
            </MDTypography>
          </MDBox>
        )}
        {customModal}       
      </>
    );
  }

  if (status === "success" && embedUrl) {
    return (
      <MDBox
        sx={{
          position: isExpanded ? "fixed" : "relative",
          inset: isExpanded ? 0 : "auto",
          zIndex: isExpanded ? 2000 : "auto",
          backgroundColor: isExpanded ? "background.default" : "transparent",
          p: isExpanded ? 0 : 2,
        }}
      >
        <MDBox
          sx={{
            position: isExpanded ? "fixed" : "absolute",
            top: isExpanded ? 20 : 25,
            right: isExpanded ? 20 : 20,
            zIndex: 2001,
            display: "flex",
            flexDirection: "row",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          { !isExpanded &&  
           <Tooltip title="Run LendPathway Analysis" placement="bottom" arrow>
            {isRunningLendPathway ? (
              <Autorenew
                onClick={() => setShowLendPathwayModal(true)}
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  fontSize: "30px !important",
                  animation: "spin 1.5s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            ) : (
              <Add
              onClick={() =>  setShowLendPathwayModal(true)}
              sx={{
                color: "primary.main",
                cursor: "pointer",
                fontSize: "32px !important",
                "& svg": {
                  strokeWidth: 3,
                  stroke: "currentColor",
                  filter: "drop-shadow(0px 1px 1px rgba(0,0,0,0.1))",
                },
                "&:hover": {
                  color: "primary.dark",
                  "& svg": {
                    strokeWidth: 5,
                  },
                },
              }}
            />
            )}
          </Tooltip>
          }
  
          <Tooltip title={isExpanded ? "Exit Full Screen" : "Expand to Full Screen"} placement="bottom" arrow>
            {isExpanded ? (
               <Tooltip title="Exit Full Screen" placement="left" arrow>
               <MDButton
                 variant="contained"
                 color="error"
                 onClick={() => setIsExpanded(false)}
                 sx={{
                   position: "fixed",
                   top: 20,
                   right: 20,
                   zIndex: 2001,
                   minWidth: "auto",
                   width: 40,
                   height: 40,
                   borderRadius: "50%",
                   p: 0,
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                   boxShadow: 4,
                   "&:hover": {
                     boxShadow: 6,
                     transform: "scale(1.05)",
                   },
                   transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                 }}
               >
                 <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                   <path
                     d="M9 9l6 6m0-6l-6 6M4 8V4m0 0h4M4 4l6 6m10-6h-4m4 0v4m0-4l-6 6M4 16v4m0 0h4M4 20l6-6m10 6h-4m4 0v-4m0 4l-6-6"
                     stroke="white"
                     strokeWidth={1.8}
                     strokeLinecap="round"
                     strokeLinejoin="round"
                   />
                 </svg>
               </MDButton>
             </Tooltip>
            ) : (
              <Fullscreen
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  fontSize: "30px !important",
                  "&:hover": {
                    color: "primary.dark",
                  },
                }}
              />
            )}
          </Tooltip>
        </MDBox>
  
        <MDBox
          sx={{
            width: "100%",
            height: isExpanded ? "100vh" : "calc(100vh - 299px)",
            borderRadius: isExpanded ? 0 : "12px",
            overflow: "hidden",
            border: isExpanded ? "none" : "1px solid rgba(0,0,0,0.08)",
            boxShadow: isExpanded ? "none" : "0 2px 12px rgba(0,0,0,0.04)",
            position: "relative",
            transition: "all 0.3s ease",
          }}
        >
          <iframe
            src={embedUrl}
            title="LendPathway Viewer"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
          />
        </MDBox>
        {customModal}       
      </MDBox>
    );
  }

  return null;
}
